import { IconDefinition } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Box, Stack, Typography } from '@vapor/material'
import { useMemo } from 'react'
import { HiCIcon } from 'src/app/components/icons/icon'

export type HicChipProps = {
  text: string
  color?: string
  variant?: 'light' | 'dark'
  icon?: IconDefinition
  onClick?: () => void
  textStyle?: 'uppercase' | 'normal'
  borderStyle?: 'none' | 'solid' | 'dashed'
  borderColor?: string
  title?: string
  image?: JSX.Element
  textColor?: string
  outlined?: boolean
  fontSize?: string
}

const outlinedStyle = {
  textStyle: 'normal',
  color: 'white',
  borderColor: 'var(--QuickSilver)',
} as const

export function HicChip({ text, color, variant, icon, onClick, textStyle = 'uppercase', borderStyle = 'none', borderColor, title, image, textColor, outlined, fontSize }: HicChipProps) {
  const defaultColor = useMemo(() => {
    if (outlined) return outlinedStyle.color
    if (color) return color
    if (variant === 'light') return 'var(--QuickSilver)'
    if (variant === 'dark') return 'white'
  }, [color, variant])

  const formattedText = useMemo(() => {
    if (outlined) return text
    if (textStyle === 'uppercase') return text.toUpperCase()
    return text
  }, [text, textStyle])

  const border = useMemo(() => {
    if (outlined) return `solid 1px ${outlinedStyle.borderColor}`
    if (borderStyle === 'solid') {
      const c = borderColor ?? 'var(--DarkImperialBlue)'
      return `solid 1px ${c}`
    }
    if (borderStyle === 'dashed') {
      const c = borderColor ?? 'var(--DarkImperialBlue)'
      return `dashed 1px ${c}`
    }
  }, [borderStyle, borderColor])

  return (
    <Box sx={{ display: 'inline-flex', px: 0.5, borderRadius: '5px', height: '18px', cursor: onClick ? 'pointer' : undefined, backgroundColor: defaultColor, border: border }} onClick={onClick}>
      <IconText icon={icon} text={formattedText} variant={variant} title={title} image={image} textColor={textColor} fontSize={fontSize} />
    </Box>
  )
}

function IconText({
  icon,
  text,
  variant,
  title,
  image,
  textColor,
  fontSize,
}: {
  icon?: IconDefinition
  text: string
  variant?: 'light' | 'dark'
  title?: string
  image?: JSX.Element
  textColor?: string
  fontSize?: string
}) {
  const iconStyle = { height: '10px' }

  return (
    <Stack direction="row" alignItems="center" spacing={0.5} height={'18px'}>
      {image && image}
      {icon && (
        <>
          {!variant || variant === 'dark' ? (
            textColor ? (
              <FontAwesomeIcon icon={icon} style={iconStyle} color={textColor} />
            ) : (
              <HiCIcon>
                <FontAwesomeIcon icon={icon} style={iconStyle} />
              </HiCIcon>
            )
          ) : (
            <FontAwesomeIcon icon={icon} color={textColor ? textColor : 'white'} style={iconStyle} />
          )}
        </>
      )}
      <Typography
        variant="caption"
        title={title ?? text}
        sx={{
          color: textColor ? textColor : variant && variant === 'light' ? 'white' : 'black',
          fontFamily: 'Cairo',
          maxWidth: 250,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          fontSize,
        }}
      >
        {text}
      </Typography>
    </Stack>
  )
}
